import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["id", "name", "rows", "value"];
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'RedactorTextEditor',
    props: /*@__PURE__*/ _mergeModels({
        id: {},
        name: { default: "" },
        rows: { default: 5 },
        minHeight: { default: 100 },
        editorClasses: { default: "" },
        configs: { default: () => [] }
    }, {
        "modelValue": { default: "" },
        "modelModifiers": {},
    }),
    emits: ["update:modelValue"],
    setup(__props, { expose: __expose }) {
        const $ = window.$;
        const props = __props;
        const model = _useModel(__props, "modelValue");
        const textarea = ref();
        // Plain variables (not refs) — these are internal bookkeeping, not rendered in the template
        let currentVal = "";
        // Guards against feedback loops: when the component programmatically sets the editor's HTML
        // (e.g. watcher sync or mount), the Redactor changeCallback should not re-emit to the parent.
        // Plain variable (not ref) since this guard flag is internal bookkeeping and does not need reactivity.
        let isProgrammaticChange = false;
        // Resolves the promise returned by update()/flush(), swapped on each async editor operation
        let changedFn = () => { };
        // Redactor's sync callback — only propagates to parent when the change originates from user input
        const changeCallback = (val) => {
            currentVal = val;
            if (!isProgrammaticChange) {
                model.value = val;
            }
            changedFn(true);
        };
        const injectValue = (value) => {
            updateValue(value, true);
        };
        const refreshValue = () => {
            $(textarea.value).redactor("code.set", model.value);
        };
        const setFocusStart = () => {
            $(textarea.value).redactor("focus.start");
        };
        const setFocusEnd = () => {
            $(textarea.value).redactor("focus.end");
        };
        const getEditor = () => {
            return $(textarea.value).redactor("core.editor");
        };
        // Forces Redactor to synchronize its internal HTML with the textarea, resolving true if a sync occurred
        const flush = async () => {
            const promise = new Promise((resolve) => {
                changedFn = resolve;
            });
            const html = getEditor().html();
            const htmlCleaned = $(textarea.value).redactor("code.cleaned", html);
            // is there a need to synchronize
            if ($(textarea.value).redactor("code.isSync", htmlCleaned)) {
                // do not sync
                changedFn(false);
            }
            else {
                $(textarea.value).redactor("code.sync");
            }
            return promise;
        };
        // Low-level editor write: insert appends at cursor, replace overwrites entire content (guarded by isProgrammaticChange)
        const update = (value, insert) => {
            const promise = new Promise((resolve) => {
                changedFn = resolve;
            });
            if (insert) {
                $(textarea.value).redactor("insert.html", value);
            }
            else {
                isProgrammaticChange = true;
                $(textarea.value).redactor("code.set", value);
            }
            return promise;
        };
        // High-level update: writes value to editor, clears the programmatic guard, and refreshes CodeMirror
        const updateValue = async (value, insert) => {
            if (insert && !getEditor().is(":visible")) {
                // Can't focus - set caret to end
                $(textarea.value).redactor("focus.end");
            }
            await update(value, insert);
            isProgrammaticChange = false;
            try {
                $(textarea.value).redactor("codemirror.refresh");
            }
            catch (e) {
                console.log(e);
            }
        };
        // Sync parent v-model changes into the editor, skipping if the editor already holds this value
        watch(model, (newValue) => {
            if (newValue !== currentVal) {
                currentVal = newValue;
                updateValue(newValue, false);
            }
        });
        // Initialize Redactor inside a programmatic guard so the initial content set doesn't emit back to parent
        onMounted(() => {
            isProgrammaticChange = true;
            $(textarea.value).redactor($.extend({
                minHeight: props.minHeight,
                editorClasses: props.editorClasses,
                changeCallback: changeCallback,
                toolbarFixedTarget: props.id,
                nativeEmojis: true
            }, ...props.configs));
            currentVal = model.value;
            isProgrammaticChange = false;
        });
        onBeforeUnmount(() => {
            $(textarea.value).redactor("core.destroy");
        });
        __expose({
            injectValue, // Can be called by parent components to inject placeholders
            refreshValue, // Can be called by parent to force refresh of value from props
            setFocusStart,
            setFocusEnd,
            getEditor,
            flush,
            textarea
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("textarea", {
                    id: _ctx.id,
                    ref_key: "textarea",
                    ref: textarea,
                    class: "form-control",
                    name: _ctx.name,
                    rows: _ctx.rows,
                    value: model.value
                }, null, 8, _hoisted_1)
            ]));
        };
    }
});
